import { useState, useEffect } from 'react';
import { useAuth } from '@/features/auth';
import {
  getPartnerConnection,
  getPartnerProfile,
  getInviteExpiresAt,
  isInvitePending,
  type PartnerProfile,
  type PartnerConnection,
} from '../services/partnerService';

interface UsePartnerResult {
  partner: PartnerProfile | null;
  connection: PartnerConnection | null;
  isConnected: boolean;
  /** true nur bei einer ausstehenden Einladung, die noch gültig ist */
  isPending: boolean;
  inviteToken: string | null;
  /** Ablaufzeitpunkt der ausstehenden Einladung, null wenn keine gültige existiert */
  inviteExpiresAt: Date | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePartner(pregnancyId: string | null): UsePartnerResult {
  const { user } = useAuth();
  const [partner, setPartner] = useState<PartnerProfile | null>(null);
  const [connection, setConnection] = useState<PartnerConnection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPartnerData = async () => {
    console.log('[usePartner] fetchPartnerData called, pregnancyId:', pregnancyId, 'userId:', user?.id);

    if (!pregnancyId || !user) {
      console.log('[usePartner] No pregnancyId or user, skipping');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get connection
      console.log('[usePartner] Fetching partner connection for pregnancy:', pregnancyId);
      const { data: connectionData, error: connectionError } = await getPartnerConnection(pregnancyId);

      console.log('[usePartner] Connection result:', connectionData, 'Error:', connectionError);

      if (connectionError && connectionError.code !== 'PGRST116') {
        // PGRST116 = no rows found, which is okay
        console.error('[usePartner] Connection error:', connectionError);
        setError('Fehler beim Laden der Partner-Verbindung');
        setIsLoading(false);
        return;
      }

      setConnection(connectionData as PartnerConnection | null);

      // If connected, get partner profile
      if (connectionData?.status === 'active') {
        console.log('[usePartner] Connection active, fetching partner profile');
        const { data: partnerData } = await getPartnerProfile(pregnancyId, user.id);
        console.log('[usePartner] Partner profile:', partnerData);
        setPartner(partnerData);
      } else {
        console.log('[usePartner] No active connection, status:', connectionData?.status);
        setPartner(null);
      }
    } catch (err) {
      console.error('[usePartner] Exception:', err);
      setError('Fehler beim Laden der Partner-Daten');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPartnerData();
  }, [pregnancyId, user?.id]);

  // Eine Einladung älter als 7 Tage gilt nicht mehr als ausstehend - der
  // Nutzer darf dann direkt eine neue erstellen.
  const hasPendingInvite = isInvitePending(connection);

  return {
    partner,
    connection,
    isConnected: connection?.status === 'active',
    isPending: hasPendingInvite,
    inviteToken: hasPendingInvite ? connection!.invitation_token : null,
    inviteExpiresAt:
      hasPendingInvite && connection ? getInviteExpiresAt(connection.created_at) : null,
    isLoading,
    error,
    refetch: fetchPartnerData,
  };
}
